import { z } from 'zod';
import { safeFileNameSchema } from '../dms/upload.schema';

/**
 * Dateien am Montage-Einsatz (ADR 0087) — der Austausch mit dem
 * Montage-Partner in **beide** Richtungen: Zeichnungen, Einweisung und
 * Sicherheitsunterlagen hinaus, Fotos und Nachweise zurück.
 *
 * Die Datei hängt am **Einsatz**, nicht am Projekt. Ein Einsatz gehört genau
 * einem Partner (ADR 0085), ein Projekt kann mehrere haben — so folgt die
 * Sichtbarkeit der Zuordnung, die es ohnehin gibt, statt eine zweite Regel
 * einzuführen, die man vergessen kann.
 */

/** 50 MB. Zeichnungen und Fotos vom Handy passen; ein Video soll es nicht. */
export const MAX_ASSEMBLY_DOCUMENT_BYTES = 52_428_800;

/**
 * Erlaubte Dateiarten. Eine **Positivliste**, weil die Dateien nach außen
 * gehen und von außen kommen: HTML oder SVG im Speicher wären ein
 * XSS-Vektor, ausführbare Anhänge ein Verteilweg.
 */
export const ASSEMBLY_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/webp',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

export function isAllowedAssemblyDocumentMime(mimeType: string): boolean {
  return (ASSEMBLY_DOCUMENT_MIME_TYPES as readonly string[]).includes(mimeType.toLowerCase());
}

/** Herkunft der Datei — Vorgabe von uns oder Nachweis von der Baustelle. */
export const assemblyDocumentSourceSchema = z.enum(['INTERN', 'PARTNER']);
export type AssemblyDocumentSource = z.infer<typeof assemblyDocumentSourceSchema>;

/** Metadaten beim Hochladen (die Bytes kommen als Rohbody). */
export const uploadAssemblyDocumentSchema = z.object({
  fileName: safeFileNameSchema,
  caption: z.string().trim().max(300).optional(),
  /**
   * Nur intern setzbar. Standard `true`: Eine Datei, die am Einsatz eines
   * Partners liegt, ist im Regelfall für ihn gedacht — sonst hätte sie dort
   * nichts zu suchen. Wer eine interne Anlage anhängt, wählt es ab, und die
   * Oberfläche zeigt den Schalter offen an, statt ihn zu verstecken.
   */
  isPartnerVisible: z.boolean().optional(),
});
export type UploadAssemblyDocumentInput = z.infer<typeof uploadAssemblyDocumentSchema>;

export const updateAssemblyDocumentSchema = z
  .object({
    caption: z.string().trim().max(300).nullable(),
    isPartnerVisible: z.boolean(),
  })
  .partial();
export type UpdateAssemblyDocumentInput = z.infer<typeof updateAssemblyDocumentSchema>;

export const assemblyDocumentResponseSchema = z.object({
  id: z.string().uuid(),
  assemblyTaskId: z.string().uuid(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  caption: z.string().nullable(),
  source: assemblyDocumentSourceSchema,
  isPartnerVisible: z.boolean(),
  /** Wer hochgeladen hat, als Klarname — bei Partner-Uploads der Firmenname. */
  uploadedByName: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type AssemblyDocumentResponse = z.infer<typeof assemblyDocumentResponseSchema>;

/**
 * Dieselbe Datei, wie der **Partner** sie im Portal sieht. Ohne
 * `isPartnerVisible` (er sieht ohnehin nur Sichtbares — der Schalter ist
 * unsere Angelegenheit) und ohne `uploadedByName`: Wer bei uns im Haus die
 * Zeichnung hochgeladen hat, ist eine interne Personalangabe.
 */
export const portalAssemblyDocumentSchema = z.object({
  id: z.string().uuid(),
  assemblyTaskId: z.string().uuid(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  caption: z.string().nullable(),
  /** `PARTNER` = von dir hochgeladen, `INTERN` = von uns bereitgestellt. */
  source: assemblyDocumentSourceSchema,
  createdAt: z.string().datetime(),
});
export type PortalAssemblyDocument = z.infer<typeof portalAssemblyDocumentSchema>;

/** Lesbare Dateigröße (de-DE) — „2,4 MB" statt „2517948". */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toLocaleString('de-DE', { maximumFractionDigits: value < 10 ? 1 : 0 })} ${units[unitIndex]}`;
}
